import { isAxiosError } from 'axios';

import type {
  V1DictionaryEntry,
  V1SecretPayloadField,
  V1SecretSchema,
  V1ValueType,
} from '@/generated/hooks/gringotts/models';

import { ECredentialScope, type TCredential } from './types';

/** Gringotts' maximum page size; one auth method's credentials fit in a page. */
export const GRINGOTTS_MAX_PAGE_SIZE = 1000;

/**
 * The scope a credential lives in, from its `scope` text
 * (`organizations/{org}` or `organizations/{org}/workspaces/{ws}`).
 *
 * @param credential - The credential.
 * @returns The scope, or `null` for anything else (e.g. `shared`).
 */
export const getCredentialScope = (
  credential: TCredential,
): ECredentialScope | null => {
  const scope = credential.scope ?? '';
  if (/^organizations\/[^/]+\/workspaces\/[^/]+$/.test(scope)) {
    return ECredentialScope.Workspace;
  }
  if (/^organizations\/[^/]+$/.test(scope)) {
    return ECredentialScope.Organization;
  }
  return null;
};

/**
 * Whether a connection in the given workspace can use the credential: it lives
 * in the organization itself or in that same workspace.
 *
 * @param credential - The credential.
 * @param organizationId - Current organization id.
 * @param workspaceId - Current workspace id.
 * @returns True when usable from the workspace.
 */
export const isCredentialUsableInWorkspace = (
  credential: TCredential,
  organizationId: string,
  workspaceId: string,
): boolean => {
  const orgScope = `organizations/${organizationId}`;
  return (
    credential.scope === orgScope ||
    credential.scope === `${orgScope}/workspaces/${workspaceId}`
  );
};

/**
 * AIP-160 filter for credentials of one type. Gringotts filters by the type's
 * id, not its name.
 *
 * @param secretTypeId - The secret type's id.
 * @returns The filter expression.
 */
export const secretTypeFilter = (secretTypeId: string): string =>
  `secret_type_id = "${secretTypeId}"`;

/**
 * Default display name for a credential created while connecting, e.g.
 * `Salesforce – Acme prod`.
 *
 * @param integrationName - Integration display name.
 * @param connectionName - The connection being created.
 * @returns The suggested display name.
 */
export const defaultCredentialDisplayName = (
  integrationName: string,
  connectionName: string,
): string => [integrationName, connectionName].filter(Boolean).join(' – ');

/**
 * Whether a schema field holds an open key/value map. Optional and sensitive
 * wrappers are unwrapped first.
 *
 * @param valueType - The field's value type.
 * @returns True for a dictionary field.
 */
const isDictionaryValueType = (valueType?: V1ValueType): boolean => {
  let current = valueType;
  while (current) {
    if (current.generic_dictionary_type) return true;
    current = current.optional_type?.inner ?? current.sensitive_type?.inner;
  }
  return false;
};

const toDictionaryEntries = (json: string): V1DictionaryEntry[] => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return [];
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return [];
  return Object.entries(parsed).map(([key, value]) => ({
    key: { text: key },
    value: { text: String(value) },
  }));
};

/**
 * Maps form values to the payload fields of a credential version. Empty values
 * are dropped. Dictionary fields arrive as JSON text from the key/value editor
 * and are sent as real dictionaries, as the type's schema requires.
 *
 * @param values - Field id → value.
 * @param schema - The credential type's schema.
 * @returns The payload fields.
 */
export const toPayloadFields = (
  values: Record<string, string>,
  schema?: V1SecretSchema,
): V1SecretPayloadField[] => {
  const dictionaryKeys = new Set(
    (schema?.fields ?? [])
      .filter(field => isDictionaryValueType(field.value))
      .map(field => field.key),
  );
  return Object.entries(values)
    .filter(([, value]) => value !== '')
    .map(([key, value]) => ({
      key,
      value: dictionaryKeys.has(key)
        ? {
            dictionary_value: {
              inline: { entries: toDictionaryEntries(value) },
            },
          }
        : { text: value },
    }));
};

/**
 * Whether a create failed because a credential with the same name already
 * exists in that scope (the name is derived from the display name).
 *
 * @param error - The thrown error.
 * @returns True for a name conflict.
 */
export const isCredentialNameConflict = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 409;

/** How many numbered names to try before giving up on a name conflict. */
const MAX_NAME_ATTEMPTS = 5;

/**
 * Runs a create under `baseName`, or under `baseName (2)`, `(3)`, … when the
 * name is taken, and finally under a random suffix. For creates the user didn't
 * name (e.g. one-click sign-in), where there's no name field to report a
 * conflict on.
 *
 * @param baseName - The preferred name.
 * @param create - Creates the credential under the given name.
 * @returns What `create` returns for the first free name.
 */
export const createWithAvailableName = async <T>(
  baseName: string,
  create: (name: string) => Promise<T>,
): Promise<T> => {
  for (let attempt = 1; attempt <= MAX_NAME_ATTEMPTS; attempt++) {
    const name = attempt === 1 ? baseName : `${baseName} (${attempt})`;
    try {
      return await create(name);
    } catch (error) {
      if (!isCredentialNameConflict(error)) throw error;
    }
  }
  return create(`${baseName} (${Math.random().toString(36).slice(2, 8)})`);
};
